import React, { Fragment, useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Radio,
  Typography,
  RadioGroup,
  FormControlLabel,
  Button,
  TextField
} from '@mui/material'
import { VasRule } from '../../../interfaces/VasRule/VasRule'
import { GenericTableV2 } from '../../../components/_EOS/GenericTable/GenericTableV2'
import { usePagination } from '../../../hooks/usePagination'
import { getColumnsConfigVas } from '../config/tableColumns'
import { useApi } from '../../../hooks/useApi'
import { ApiResponse } from '../../../typings/ApiResponse'
import { Status } from '../../../hooks/useFetchState'
import {
  getVasRuleAvailable,
  getVasRuleAssignable,
  vasRuleUnassignToSku,
  vasRuleAssignToSku
} from '../config/api'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal } from '../../../hooks/useModal'
import { Filter } from '../../../components/_WMS/FilterComponent'
import { ActyonMode } from '../types'

interface ConfigProductModalProps {
  skuId: number
  sku: string
  handleSnackbar: (message: string, success: boolean) => void
  requestProduct: (filters?: Filter[], page?: number, pageSize?: number) => void
}

export const VasRuleConfigModal: React.FC<ConfigProductModalProps> = ({
  handleSnackbar,
  sku,
  skuId,
  requestProduct
}) => {
  const [searchName, setSearchName] = useState<string>('')
  const [value, setValue] = React.useState<ActyonMode>('ADD')
  const { callApi, state } = useApi<ApiResponse<VasRule>>()
  const { callApi: callApiUpdate, state: stateUpdate } = useApi()
  const [selectedVasName, seSelectedVasName] = useState<string>('')

  const [selectedElements, setSelectedElements] = useState<number[]>([])
  const columnsConfig = getColumnsConfigVas()

  const pagination = usePagination(5)
  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    pagination.setPagination(auxPage, pageSize)
  }

  const handleConfirm = () => {
    if (selectedElements.length !== 1) {
      handleSnackbar('Debes seleccionar una única regla VAS', false)
      return
    }

    const vasRuleId = selectedElements[0]

    const action =
      value === 'REMOVE'
        ? vasRuleUnassignToSku(vasRuleId, skuId)
        : vasRuleAssignToSku(vasRuleId, skuId)

    const successMessage = 'Gestión realizada exitosamente'

    const errorMessage =
      value === 'REMOVE' ? 'Error eliminando regla' : 'Error asignando regla'

    callApiUpdate(action)
      .then(() => {
        handleSnackbar(successMessage, true)
        cleanElements()
        requestData(0, pagination.pageSize)
        requestProduct()
      })
      .catch((error) => {
        handleSnackbar(`${errorMessage}: ${error}`, false)
      })
      .finally(() => {
        confirmModal.closeModal()
      })
  }

  const requestData = (page: number, pageSize: number, name?: string) => {
    if (value === 'REMOVE') {
      callApi(getVasRuleAvailable(skuId, page, pageSize, name)).catch(
        (error) => {
          handleSnackbar(error, false)
        }
      )
    } else {
      callApi(getVasRuleAssignable(skuId, page, pageSize, name)).catch(
        (error) => {
          handleSnackbar(error, false)
        }
      )
    }
  }

  const cleanElements = () => {
    setSearchName('')
    setSelectedElements([])
    pagination.setPagination(0, pagination.pageSize)
  }

  const cleanSearch = () => {
    cleanElements()
    requestData(0, pagination.pageSize)
  }

  useEffect(() => {
    cleanElements()
  }, [value])

  useEffect(() => {
    requestData(pagination.page, pagination.pageSize, searchName)
  }, [pagination.page, pagination.pageSize, value])

  const handleSearch = (name: string) => {
    setSelectedElements([])
    pagination.setPagination(0, pagination.pageSize)
    requestData(0, pagination.pageSize, name)
  }

  const confirmModal = useModal('')

  useEffect(() => {
    if (selectedElements.length === 1 && state.data?.items) {
      const selected = state.data.items.find(
        (v) => v.vasRuleId === selectedElements[0]
      )

      seSelectedVasName(selected?.name || '')
    } else {
      seSelectedVasName('')
    }
  }, [selectedElements])

  return (
    <Fragment>
      <Typography color='textSecondary'>
        Selecciona qué deseas configurar para este producto.
      </Typography>

      <RadioGroup
        value={value}
        onChange={(e) => setValue(e.target.value as 'ADD' | 'REMOVE')}
      >
        <Paper
          variant='outlined'
          style={{
            width: '96%',
            padding: 6,
            marginTop: 6
          }}
        >
          <FormControlLabel
            value='ADD'
            control={<Radio />}
            label={
              <Typography variant='subtitle1'>Asociar Regla VAS</Typography>
            }
          />
        </Paper>

        <Paper
          variant='outlined'
          style={{
            width: '96%',
            padding: 6,
            marginTop: 6
          }}
        >
          <FormControlLabel
            value='REMOVE'
            control={<Radio />}
            label={
              <Typography variant='subtitle1'>Eliminar Regla VAS</Typography>
            }
          />
        </Paper>
      </RadioGroup>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '24px'
        }}
      >
        <TextField
          label='Buscar regla VAS'
          variant='outlined'
          size='small'
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />

        <Button
          variant='contained'
          color='primary'
          onClick={() => handleSearch(searchName)}
        >
          Buscar
        </Button>
        <Button
          variant='contained'
          color='inherit'
          onClick={() => cleanSearch()}
        >
          Limpiar
        </Button>
      </Box>

      <GenericTableV2<VasRule>
        data={state.data?.items || []}
        columns={columnsConfig}
        keyField='vasRuleId'
        loading={state.status === Status.Loading}
        selection={{
          selectedIds: selectedElements,
          onSelectionChange: (ids) => setSelectedElements(ids as number[]),
          isSingleSelection: true
        }}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          total: state.data?.totalItems || 0,
          onPageChange: handlePagination
        }}
        styling={{
          dense: true,
          hoverEffect: true
        }}
      />
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '24px'
        }}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={() =>
            confirmModal.openModal(
              'sm',
              undefined,
              `${value === 'REMOVE' ? 'Eliminar' : 'Asociar'}  Regla VAS`
            )
          }
          disabled={selectedElements.length !== 1 || !value}
        >
          Confirmar
        </Button>
      </Box>

      <Modal
        open={confirmModal.isOpen}
        maxWidth={confirmModal.size}
        onClose={confirmModal.closeModal}
        title={confirmModal.title}
        loading={stateUpdate.status === Status.Loading}
        cancelButton={{
          action: () => confirmModal.closeModal(),
          title: 'Cancelar'
        }}
        successButton={{
          action: handleConfirm,
          title: 'Confirmar'
        }}
      >
        <Typography variant='body2' component={'h4'}>
          {`Desea ${
            value === 'REMOVE' ? 'Eliminar' : 'Asociar'
          } la regla VAS ${selectedVasName} al sku ${sku}. ¿Desea continuar?`}
        </Typography>
      </Modal>
    </Fragment>
  )
}
